/**
 * Client-side JSON Schema checking for the config editor.
 *
 * This is deliberately a *subset* validator, not a second implementation of the
 * server's Ajv. Its only job is to drive the form: to decide which field gets a
 * red border and what the inline message says, live, on every keystroke. The
 * server remains the authority — a save that passes here can still come back
 * 400, and {@link violationsToPaths} exists to paint those answers onto the same
 * fields.
 *
 * Keeping it a subset is the honest trade. Ajv supports allOf/oneOf/$ref and
 * much else; the form renderer supports objects, arrays of scalars, enums and
 * primitives. Anything outside that falls through to a raw JSON textarea, where
 * pretending to validate would be worse than not trying.
 */

export const SCHEMA_TYPES = ['object', 'array', 'string', 'integer', 'number', 'boolean'] as const;

export interface ValidationError {
  /** '$'-notation, e.g. `$.cloud_config.region` or `$.roles[0]`. */
  path: string;
  message: string;
}

/** Builds the display path for a value location. Array steps render as `[i]`. */
export const pathToString = (path: (string | number)[]): string =>
  path.length
    ? `$.${path.map((p) => (typeof p === 'number' ? `[${p}]` : p)).join('.')}`.replace(/\.\[/g, '[')
    : '$';

/**
 * Converts a server violation's JSON Pointer to the notation above.
 *
 * Ajv reports `/cloud_config/region`; the form indexes rows by
 * `$.cloud_config.region`. Without this the server's answer would arrive with
 * nowhere to render, and the operator would see a generic "save failed" while
 * the offending field sat there unmarked.
 */
export const pointerToPath = (pointer: string): string => {
  if (!pointer || pointer === '/') return '$';

  const segments = pointer.replace(/^\//, '').split('/');

  return segments.reduce<string>((acc, segment) => {
    // Un-escape JSON Pointer's two reserved sequences before display.
    const key = segment.replace(/~1/g, '/').replace(/~0/g, '~');
    return /^\d+$/.test(key) ? `${acc}[${key}]` : `${acc}.${key}`;
  }, '$');
};

/** Validates a value against a schema node, accumulating every failure. */
export const validateValue = (
  value: unknown,
  schema: unknown,
  path: (string | number)[] = [],
  errors: ValidationError[] = [],
): ValidationError[] => {
  const here = pathToString(path);

  if (!schema || typeof schema !== 'object') return errors;

  const node = schema as Record<string, any>;
  const fail = (message: string) => {
    errors.push({ path: here, message });
    return errors;
  };

  if (node.type === 'object') {
    if (value === null || typeof value !== 'object' || Array.isArray(value)) {
      return fail('Expected an object');
    }

    const properties: Record<string, unknown> = node.properties || {};
    const record = value as Record<string, unknown>;

    for (const key of node.required || []) {
      if (!(key in record)) {
        errors.push({ path: pathToString([...path, key]), message: 'Required key is missing' });
      }
    }

    for (const key of Object.keys(record)) {
      if (!(key in properties)) {
        // Surfaced rather than ignored: an unknown key is usually a typo in a
        // key that *is* required, and silently dropping it from the form would
        // hide the operator's data from them.
        errors.push({
          path: pathToString([...path, key]),
          message: 'Key is not defined in the schema',
        });
      } else {
        validateValue(record[key], properties[key], [...path, key], errors);
      }
    }

    return errors;
  }

  if (node.type === 'array') {
    if (!Array.isArray(value)) return fail('Expected an array');
    if (node.minItems != null && value.length < node.minItems) {
      fail(`At least ${node.minItems} item(s) required`);
    }
    if (node.maxItems != null && value.length > node.maxItems) {
      fail(`At most ${node.maxItems} item(s) allowed`);
    }
    if (node.items) {
      value.forEach((item, index) => validateValue(item, node.items, [...path, index], errors));
    }
    return errors;
  }

  if (node.type === 'boolean' && typeof value !== 'boolean') return fail('Expected true or false');

  if (node.type === 'integer' || node.type === 'number') {
    if (typeof value !== 'number' || Number.isNaN(value)) return fail('Expected a number');
    if (node.type === 'integer' && !Number.isInteger(value)) return fail('Expected a whole number');
    if (node.minimum != null && value < node.minimum) fail(`Must be at least ${node.minimum}`);
    if (node.maximum != null && value > node.maximum) fail(`Must be at most ${node.maximum}`);
  }

  if (node.type === 'string') {
    if (typeof value !== 'string') return fail('Expected text');
    if (node.minLength != null && value.length < node.minLength) {
      fail(`Must be at least ${node.minLength} characters`);
    }
    if (node.maxLength != null && value.length > node.maxLength) {
      fail(`Must be at most ${node.maxLength} characters`);
    }
    if (node.pattern && !new RegExp(node.pattern).test(value)) {
      fail(`Must match ${node.pattern}`);
    }
  }

  if (node.enum && Array.isArray(node.enum) && !node.enum.includes(value as never)) {
    fail(`Must be one of: ${node.enum.join(', ')}`);
  }

  return errors;
};

/**
 * Validates a *schema* the operator is authoring.
 *
 * Stricter than Ajv on purpose. Ajv accepts a node with no `type` at all and
 * treats it as "anything", which compiles fine and then renders as an
 * un-editable raw textarea. Requiring a type here means the create form can
 * promise that what you author is what the form can edit.
 */
export const validateSchema = (
  schema: unknown,
  path = '$',
  errors: ValidationError[] = [],
): ValidationError[] => {
  if (!schema || typeof schema !== 'object' || Array.isArray(schema)) {
    errors.push({ path, message: 'Schema node must be an object' });
    return errors;
  }

  const node = schema as Record<string, any>;

  if (!SCHEMA_TYPES.includes(node.type)) {
    errors.push({ path, message: `type must be one of ${SCHEMA_TYPES.join(', ')}` });
    return errors;
  }

  if (node.type === 'object') {
    if (!node.properties || typeof node.properties !== 'object') {
      errors.push({ path, message: 'object needs "properties"' });
    } else {
      for (const key of Object.keys(node.properties)) {
        validateSchema(node.properties[key], `${path}.${key}`, errors);
      }
    }

    if (node.required) {
      if (!Array.isArray(node.required)) {
        errors.push({ path: `${path}.required`, message: 'must be an array of keys' });
      } else {
        for (const key of node.required) {
          if (!node.properties || !(key in node.properties)) {
            errors.push({ path: `${path}.required`, message: `"${key}" is not a property` });
          }
        }
      }
    }
  }

  if (node.type === 'array') {
    if (!node.items) errors.push({ path, message: 'array needs "items"' });
    else validateSchema(node.items, `${path}.items`, errors);
  }

  if (node.minimum != null && node.maximum != null && node.minimum > node.maximum) {
    errors.push({ path, message: 'minimum exceeds maximum' });
  }

  if (node.enum && !Array.isArray(node.enum)) {
    errors.push({ path, message: 'enum must be an array' });
  }

  return errors;
};

/** The grey one-liner under a field: the constraints, in the order they bite. */
export const hintFor = (schema: Record<string, any>): string => {
  const parts: string[] = [schema.type];

  if (schema.minimum != null && schema.maximum != null) {
    parts.push(`${schema.minimum}–${schema.maximum}`);
  } else if (schema.minimum != null) {
    parts.push(`≥ ${schema.minimum}`);
  } else if (schema.maximum != null) {
    parts.push(`≤ ${schema.maximum}`);
  }

  if (schema.minLength != null) parts.push(`min ${schema.minLength} chars`);
  if (schema.pattern) parts.push(schema.pattern);
  if (schema.format) parts.push(schema.format);

  if (schema.type === 'array') {
    if (schema.items?.type) parts[0] = `array of ${schema.items.type}`;
    if (schema.minItems != null || schema.maxItems != null) {
      parts.push(`${schema.minItems ?? 0}–${schema.maxItems ?? '∞'} items`);
    }
  }

  return parts.join(' · ');
};

/** Maps a list of errors to `path -> first message`, which is what a row reads. */
export const toErrorMap = (errors: ValidationError[]): Record<string, string> => {
  const map: Record<string, string> = {};
  for (const error of errors) if (!map[error.path]) map[error.path] = error.message;
  return map;
};
